import { configureStore } from '@reduxjs/toolkit';
import { copilotTrackingMiddleware } from '@components/copilot/copilotTrackingMiddleware';
import { baseApi } from '@api/baseApi';
import { superAdminApi } from '@api/superAdminApi';
import uiReducer from './uiSlice';
import authReducer from './authSlice';

/**
 * Zentrix Redux Store
 * ═══════════════════════════════════════════
 *
 * Architecture:
 * - baseApi.reducer  → RTK Query cache (all API data)
 * - ui               → UI state (sidebar, modals, theme)
 * - auth             → Auth state (workspace, initialization)
 *
 * All module API endpoints are injected into baseApi via injectEndpoints()
 * in their respective api files (authApi.js, workspaceApi.js, etc.)
 */

export const store = configureStore({
  reducer: {
    // RTK Query API reducer — handles all API caching, polling, invalidation
    [baseApi.reducerPath]: baseApi.reducer,
    [superAdminApi.reducerPath]: superAdminApi.reducer,

    // UI state — sidebar, modals, theme, search, notifications
    ui: uiReducer,

    // Auth state — initialization, active workspace
    auth: authReducer,

    // ── Future module slices (uncomment as built) ──
    // project: projectReducer,
    // task: taskReducer,
    // sprint: sprintReducer,
    // kanban: kanbanReducer,
    // hivelink: hivelinkReducer,
    // notification: notificationReducer,
    // workflow: workflowReducer,
  },

  // RTK Query middleware — required for caching, polling, invalidation, refetching
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore non-serializable values in these action types
        ignoredActions: [
          'persist/PERSIST',
          'persist/REHYDRATE',
        ],
        // Ignore non-serializable values in these paths
        ignoredPaths: ['api.mutations'],
      },
    })
      .concat(baseApi.middleware)
      .concat(superAdminApi.middleware)
      // Emits copilot actions from real API traffic — save_success, save_error,
      // error_shown, search_run, search_empty — so trigger rules built on them
      // can fire without per-screen wiring.
      .concat(copilotTrackingMiddleware),

  // Enable Redux DevTools in development
  devTools: import.meta.env.DEV,
});

// Export store types for typed hooks (when migrating to TypeScript)
// export type RootState = ReturnType<typeof store.getState>;
// export type AppDispatch = typeof store.dispatch;
